/**
 * Re-orders a grid's column definitions to match a saved column layout, so the grid
 * is built with the user's order already in place instead of flashing the default one.
 *
 * Columns the user cannot move are exempt. A pinned or `lockPosition` column is put
 * back where it was declared, whatever the saved layout says: AG Grid enforces its own
 * locking, so a defs array that disagrees leaves the columns it renders out of step
 * with the columns it thinks it has - the pinned USER NAME column on the users grid
 * ended up drawn on top of the one beside it, with every later column one header off.
 *
 * @param {Array} columnDefs The grid's column definitions.
 * @param {Array} savedColumnState AG Grid column state, in the user's saved order.
 * @returns {Array} columnDefs in the saved order, locked columns left alone.
 */
const orderColumnDefs = (columnDefs, savedColumnState)=> {
  if(!Array.isArray(columnDefs) || !Array.isArray(savedColumnState) || savedColumnState.length === 0) {
    return columnDefs;
  }

  try {
    const savedIndexByColId = new Map(
      savedColumnState.map((column, index)=> [column.colId, index])
    );
    const positioned = new Array(columnDefs.length);
    const unsortedColumns = [];
    const lockedColumns = [];

    columnDefs.forEach((columnDef, defIndex)=> {
      if(columnDef.pinned || columnDef.lockPosition) {
        lockedColumns.push({columnDef, defIndex});
        return;
      }

      const index = savedIndexByColId.get(columnDef.field);
      if(index === undefined) {
        unsortedColumns.push(columnDef);
        return;
      }

      positioned[index] = columnDef;
    });

    const ordered = [...positioned.filter(Boolean), ...unsortedColumns];

    // Ascending, so an earlier locked column is seated before a later one shifts it.
    lockedColumns.forEach(({columnDef, defIndex})=> {
      ordered.splice(Math.min(defIndex, ordered.length), 0, columnDef);
    });

    return ordered;
  } catch(e) {
    return columnDefs;
  }
};

export {orderColumnDefs};
export default orderColumnDefs;
